import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useTrackPageView } from '@g4/trackster';
import moment from '@g4/moment-utc';
import { v4 as uuidv4 } from 'uuid';
import UpdatePasswordBanner from '@g4/next-furniture/UpdatePassword/UpdatePasswordBanner';

import {
  withApplicationQuery,
  useTranslations,
} from '../graphql/components/ApplicationQueryProvider';

import STADIUM_TRANSLATIONS_QUERY from './stadiumPage/stadiumTranslations.query.graphql.js';
import SHOWS_QUERY from './stadiumPage/stadiumGames.query.graphql.js';
import useStadiumGame from '../hooks/useStadiumGame';
import useStadiumConfig from '../hooks/useStadiumConfig';
import useWindowLocationSearchParams from '../hooks/useWindowLocationSeachParams.js';

import {
  Box,
  Layout,
  Media,
  VerticalSpacing,
  useMedia,
} from '../components/Boneyard';
import ERROR_ICON from '../images/ic-error-dark.svg';
import ErrorBanner from './indexPage/ErrorBanner';

import OverlayMerchandise from './indexPage/OverlayMerchandise';
import { useAuthentication, useSSRQuery } from '../graphql';
import OVERLAY_CHECK_QUERY from './indexPage/overlayMerchandise/overlayCheck.query.graphql.js';
import OVERLAY_QUERY from './indexPage/overlayMerchandise/overlay.query.graphql.js';
import Markdown from '../components/Markdown';
import GameSelectionCarousel from './stadiumPage/GameSelectionCarousel';
import TermsAndConditions from './stadiumPage/TermsAndConditions';
import Disclaimer from './stadiumPage/Disclaimer';
import WrappedStadiumFlightSearchForm from './stadiumPage/WrappedStadiumFlightSearchForm';
import LANDING_PAGE_STADIUM_IMAGES from './stadiumPage/landingPageStadiumImages.query.graphql.js';

import { ALLEGIANT_STADIUM_LANDING_PAGE } from './stadiumPage/automationHooks.json';

import { withValue } from '@g4/automation-hooks';
import { showsToGames } from './stadiumPage/showsToGames';

const FormWrapper = styled.div`
  position: relative;
  width: calc(100% + ${props => props.theme.spacing(6)});
  background: linear-gradient(black, black 40%);
  padding: ${props => props.theme.spacing(4)} ${props => props.theme.spacing(3)};
  margin-top: -1px;
  margin-left: -${props => props.theme.spacing(3)};

  ${props => props.theme.query.md} {
    width: 100%;
    padding: ${props => props.theme.spacing(8)};
    border-radius: 8px;
    margin-top: 0;
    margin-left: 0;
    margin-bottom: ${props => props.theme.spacing(12)};
  }
`;

const FullWidth = styled(Layout.FullWidth)`
  position: absolute;
  height: 100%;
  background-color: #000000;
`;

const PackagesImage = styled.img`
  position: relative;
  max-width: 600px;
  margin: ${props => (props.isMobile ? '35px 0px' : '35px 30px')};
  width: ${props => (props.isMobile ? '100%' : '75%')};
  left: 0;
  transform: none;
`;

const GameStepContainer = styled.div`
  width: 100%;
  padding: ${props =>
    props.isMobile ? '0px' : props.padding ? props.padding : '40px'};
`;

const GameSelectionStep = styled.div`
  display: block;
  background: linear-gradient(
    90deg,
    transparent,
    #ffffff82,
    #ffffff61,
    #ffffff82,
    transparent
  );
  padding: 20px 0;
  text-align: center;
  font-weight: bold;
  font-size: 1.25rem !important;
  color: #fff;
`;

const FirstStep = styled.span`
  margin-top: 50px;
  margin-bottom: ${props => (props.isMobile ? '0' : '-20px')};
`;

const SecondtStep = styled.span`
  margin-top: 35px;
  margin-bottom: ${props => (props.isMobile ? '0' : '-20px')};
`;

const StadiumImage = styled.img`
  width: 100%;
`;

const StadiumPage = () => {
  useTrackPageView('/', 'Home');
  const { isAuthenticated } = useAuthentication();
  const { stadiumPackages: translations } = useTranslations();
  const mq = useMedia();
  const isMobile = mq.lte('sm');
  const queryParams = useWindowLocationSearchParams();

  const [isFlightMarketError, setIsFlightMarketError] = React.useState(false);
  const [stadiumTransactionID, setStadiumTransactionID] = React.useState('');
  const [stadiumGame, setStadiumGame] = useStadiumGame();
  const {
    isLoading: isStadiumConfigLoading,
    stadiumProductIds,
    stadiumBookingCacheCounter,
  } = useStadiumConfig();

  const [
    isOverlayMerchandiseOverTimeLimit,
    setIsOverlayMerchandiseOverTimeLimit,
  ] = React.useState(false);

  const { data: stadiumImages } = useSSRQuery(LANDING_PAGE_STADIUM_IMAGES);

  const images = stadiumImages?.application?.images;

  const allegiantStadium = images?.find(
    image => image.id === 'landingStadiumBackground'
  );

  const allegiantStadiumPackages = images?.find(
    image => image.id === 'landingStadiumBillboard'
  );

  const allegiantStadiumSeatMap = images?.find(
    image => image.id === 'landingStadiumMap'
  );

  // TODO: move these setps to the translations.
  const firstStep = 'Step 1. Select your game';
  const secondStep =
    'Step 2. Select your home airport and check available dates';

  const checkOverlays = () => {
    const savedTime =
      typeof window === 'object' &&
      window.localStorage.getItem('overlayMerchandiseDisplayExp');
    const timeDiff = moment().diff(moment(savedTime), 'hours');
    let isOverTimeLimit = false;
    if (!savedTime || timeDiff >= 0) {
      const twentyFourHoursAhead = moment().add(24, 'hours');
      typeof window === 'object' &&
        window.localStorage.setItem(
          'overlayMerchandiseDisplayExp',
          twentyFourHoursAhead.toString()
        );
      isOverTimeLimit = true;
    }
    setIsOverlayMerchandiseOverTimeLimit(isOverTimeLimit);
  };

  useEffect(() => {
    checkOverlays();
    // handle issue when react re-runs the useEffect immediately
    return () => {
      const twentyFourHoursAhead = moment().add(24, 'hours');
      const savedTime = window.localStorage.getItem(
        'overlayMerchandiseDisplayExp'
      );
      const diff = twentyFourHoursAhead.diff(moment(savedTime), 'seconds');
      const REACT_UNMOUNT_CHECK_INTERVAL = 2; // two seconds
      if (diff < REACT_UNMOUNT_CHECK_INTERVAL) {
        // react is re-running the use effect within a couple of seconds
        window.localStorage.removeItem('overlayMerchandiseDisplayExp');
      }
    };
  }, [setIsOverlayMerchandiseOverTimeLimit]);

  const overlays = useSSRQuery(OVERLAY_CHECK_QUERY);
  const { loading, error, data, refetch } = useSSRQuery(OVERLAY_QUERY, {
    fetchPolicy: 'network-only',
  });

  const { data: response } = useSSRQuery(SHOWS_QUERY, {
    skip: isStadiumConfigLoading,
    context: {
      headers: {
        'X-StadiumBookingCacheCounter': stadiumBookingCacheCounter,
      },
    },
    variables: {
      showAvailabilitySearchCriteria: {
        startDate: moment()
          .add(1, 'days')
          .format('YYYY-MM-DD'),
        endDate: moment()
          .add(1, 'years')
          .format('YYYY-MM-DD'),
        productIds: !stadiumProductIds?.[0] ? [] : stadiumProductIds,
        showConditions: [
          'PACKAGE_ID',
          'OPPONENT_NAME',
          'OPPONENT_CITY',
          'SECTION_NUMBER',
        ],
      },
    },
    onCompleted: response => {
      // When we execute the raiders query for that transaction id
      // The shopped raiders games details will going to store in redis server for that transaction id

      // DC-23634
      // This is a hotfix for the issue in production where stadium transaction id remains unchanged
      // for all logged users.
      let transactionId = uuidv4();
      transactionId = transactionId.split('-').join('');
      setStadiumTransactionID(transactionId);
    },
  });

  const showsData = response?.showsAvailability?.result;

  let currentSlideIndex = 0;
  const raidersGames = [];

  let gamesData = showsToGames(showsData) || [];

  // Sorting Gameday closest to today's
  gamesData = [...gamesData].sort((a, b) =>
    moment(a.useDate).diff(moment(b.useDate))
  );

  (gamesData ?? []).forEach(({ useDate, ...restOfGame }, index) => {
    const gameDate = moment(useDate).format('YYYY-MM-DD');
    if (
      currentSlideIndex === 0 &&
      Date.parse(gameDate) > Date.parse(new Date())
    ) {
      currentSlideIndex = index;
    }
    raidersGames.push({ ...restOfGame, date: gameDate });
  });

  const [isGameChanged, setIsGameChanged] = React.useState(false);
  const [isSold, setIsSold] = React.useState(false);

  useEffect(() => {
    refetch && refetch();
  }, [isAuthenticated, refetch]);

  useEffect(() => {
    // Clearing the stale data (selected game card details) from localstorage
    typeof window === 'object' &&
      window.localStorage.removeItem('selectedStadiumGame');
  }, []);

  if (loading) {
    return null;
  }
  if (error) {
    throw error;
  }

  const fetchOverlay = data?.application.merchandiseOverlay;
  const displayOverlay =
    overlays.data?.application.configurations.merchandiseOverlayDisplay;
  return (
    <Media>
      {mq => (
        <React.Fragment>
          {isFlightMarketError && (
            <ErrorBanner icon={ERROR_ICON} ln1={setIsFlightMarketError} />
          )}
          {isOverlayMerchandiseOverTimeLimit &&
            displayOverlay &&
            fetchOverlay.content && (
              <>
                <Markdown
                  source={fetchOverlay.content}
                  customElements={{ OverlayMerchandise }}
                />
              </>
            )}
          {queryParams?.password_updated && <UpdatePasswordBanner />}
          <FullWidth>
            <StadiumImage
              src={allegiantStadium?.path}
              alt={allegiantStadium?.alt}
            />
          </FullWidth>
          <PackagesImage
            isMobile={isMobile}
            src={allegiantStadiumPackages?.path}
            alt={allegiantStadiumPackages?.alt}
          />
          <Box
            backgroundColor="rgba(0, 0, 0, 0.65)"
            position="relative"
            d="inline-block"
            pt={isMobile ? 2 : 10}
          >
            <GameStepContainer isMobile={isMobile}>
              <GameSelectionStep>
                <FirstStep>{firstStep}</FirstStep>
              </GameSelectionStep>
            </GameStepContainer>

            {!isMobile && <VerticalSpacing size={3} />}
            <GameSelectionCarousel
              games={raidersGames}
              slideIndex={currentSlideIndex}
              raidersCityLabel={translations.homeCityLabel}
              raidersTeamNameLabel={translations.homeTeamNameLabel}
              raidersLimitedSeatsLabel={translations.limitedSeatsLabel}
              raidersSoldOutLabel={translations.soldOutLabel}
              raidersVSLabel={translations.versusLabel}
              setIsGameChanged={setIsGameChanged}
              setIsSold={setIsSold}
              gameSelectedLabel={translations.gameSelectedLabel}
              setStadiumGame={setStadiumGame}
            />
            <VerticalSpacing size={3} />

            <GameStepContainer padding={!isMobile ? '40px 0 0' : '5px 0 15px'}>
              <GameSelectionStep>
                <SecondtStep>{secondStep}</SecondtStep>
              </GameSelectionStep>
            </GameStepContainer>
            <VerticalSpacing size={3} />

            <FormWrapper>
              <WrappedStadiumFlightSearchForm
                searchResultsPath="/booking"
                displayFormFooter
                setIsFlightMarketError={setIsFlightMarketError}
                isGameChanged={isGameChanged}
                setIsGameChanged={setIsGameChanged}
                isSold={isSold}
                setIsSold={setIsSold}
                stadiumTransactionID={stadiumTransactionID}
                selectedGame={stadiumGame}
              />
            </FormWrapper>
            <VerticalSpacing size={3} />

            {isMobile && (
              <Box d="inline-block" p={2}>
                <img
                  src={allegiantStadiumSeatMap?.path}
                  alt={allegiantStadiumSeatMap?.alt}
                />
              </Box>
            )}

            <Box w="100%" d="flex">
              <Disclaimer
                raidersDisclaimerHeaderLabel={
                  translations.disclaimerHeaderLabel
                }
                raidersDisclaimerFirstBulletPointText={
                  translations.disclaimerFirstBulletPointText
                }
                raidersDisclaimerFirstBulletPointSubText={
                  translations.disclaimerFirstBulletPointSubText
                }
                raidersDisclaimerSecondBulletPointText={
                  translations.disclaimerSecondBulletPointText
                }
                raidersDisclaimerThirdBulletPointText={
                  translations.disclaimerThirdBulletPointText
                }
                isMobile={isMobile}
              />
              {!isMobile && (
                <Box
                  w="40%"
                  d="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    w="62.5%"
                    d="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <img
                      src={allegiantStadiumSeatMap?.path}
                      alt={allegiantStadiumSeatMap?.alt}
                    />
                  </Box>
                </Box>
              )}
            </Box>
            <VerticalSpacing size={isMobile ? 5 : 10} />
            <TermsAndConditions
              raidersTermsAndConditionsHeader={
                translations.termsAndConditionsHeader
              }
              raidersTermsAndConditionsContent={
                translations.termsAndConditionsContent
              }
              automationHook={withValue(
                ALLEGIANT_STADIUM_LANDING_PAGE.hook,
                'terms-and-condition'
              )}
            />
            <VerticalSpacing size={isMobile ? 5 : 20} />
          </Box>
        </React.Fragment>
      )}
    </Media>
  );
};

export default withApplicationQuery(STADIUM_TRANSLATIONS_QUERY, StadiumPage);
